import { Fragment, useEffect, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useSearchParams } from 'react-router-dom';
import { Search, Eye, Check, Ban, ExternalLink, AlertTriangle, Plus, Minus, Undo2, Pencil, Printer, Eraser, ArrowLeftRight, ShoppingCart } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import { ModalReciboTroca, type ReciboTrocaInfo } from '../../components/trocas/ModalReciboTroca';
import { ExtratoCredito, type MovimentacaoExtrato } from '../../components/trocas/ExtratoCredito';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';

interface TrocaRow {
  id: number;
  numero: string;
  cliente_id: number;
  cliente_nome: string;
  cliente_codigo: string | null;
  tipo: 'troca' | 'devolucao' | 'compra_credito';
  status: 'finalizada' | 'cancelada' | 'retornada';
  // Compra com Credito (migration 0340): o orcamento que o cliente levou.
  orcamento_numero: string | null;
  valor_devolvido: string;
  valor_levado: string;
  saldo_credito: string;
  gerar_credito: boolean;
  validada: boolean;
  validada_em: string | null;
  criado_em: string;
  operador_nome: string | null;
  cliente_whatsapp: string | null;
  cliente_telefone: string | null;
  // campos do crédito para o selo (adicionados pelo GET /api/trocas via LEFT JOIN LATERAL)
  credito_saldo: string | null;
  credito_utilizado: string | null;
  credito_status: 'ativo' | 'consumido' | 'cancelado' | null;
}

interface ItemDetalhe {
  id: number;
  direcao: 'devolvido' | 'levado';
  produto_id: number;
  codigo_interno: string | null;
  produto_descricao: string;
  foto_url: string | null;
  quantidade: string;
  preco_unitario: string;
  subtotal: string;
}

interface CreditoDetalhe {
  id: number;
  numero: string;
  valor_original: string;
  valor_utilizado: string;
  saldo: string;
  status: string;
  criado_em: string;
  movimentacoes: MovimentacaoExtrato[];
}

interface TrocaDetalhe extends TrocaRow {
  motivo: string | null;
  observacoes: string | null;
  motivo_cancelamento: string | null;
  cancelada_em: string | null;
  validada_por_nome: string | null;
  cancelada_por_nome: string | null;
  itens: ItemDetalhe[];
  credito: CreditoDetalhe | null;
  // Compra com Credito: de quais creditos saiu o pagamento ('uso') e o que voltou se cancelada ('estorno').
  creditos_usados?: Array<{
    tipo: string; valor: string; saldo_depois: string; criado_em: string;
    credito_numero: string; troca_origem_numero: string | null;
  }>;
}

// Bloco "Pago com credito" da compra com credito — no lugar do extrato de credito gerado.
function CreditosUsados({ det }: { det: TrocaDetalhe }) {
  const linhas = det.creditos_usados ?? [];
  if (det.tipo !== 'compra_credito' || linhas.length === 0) return null;
  return (
    <div>
      <h4 style={{ margin: '0 0 6px 0', fontSize: 13, color: '#16a34a' }}>
        Pago com credito{det.orcamento_numero ? ` — orcamento ${det.orcamento_numero}` : ''}
      </h4>
      <table className="tabela tabela-aninhada" style={{ width: '100%', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ width: 140 }}>Data</th>
            <th>Credito</th>
            <th>Origem</th>
            <th style={{ width: 90 }}>Movimento</th>
            <th style={{ textAlign: 'right', width: 100 }}>Valor</th>
            <th style={{ textAlign: 'right', width: 110 }}>Saldo do credito</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={i}>
              <td>{formatDataHoraSP(l.criado_em)}</td>
              <td style={{ fontFamily: 'monospace' }}>{l.credito_numero}</td>
              <td>{l.troca_origem_numero ?? '—'}</td>
              <td>{l.tipo === 'uso' ? 'Usado' : l.tipo === 'estorno' ? 'Devolvido' : l.tipo}</td>
              <td style={{ textAlign: 'right' }}>{l.tipo === 'uso' ? '−' : '+'}{formatMoeda(l.valor)}</td>
              <td style={{ textAlign: 'right' }}>{formatMoeda(l.saldo_depois)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Selo do estado do crédito na coluna Saldo da listagem.
// Pílula sólida de largura horizontal UNIFORME (min 104px), rótulo em CAIXA ALTA.
// Daltonismo-safe (verde/vermelho): distinção por TEXTO + paleta azul/amarelo/vermelho.
//   AMARELO = ainda há saldo (DISPONÍVEL ou PARCIAL) · VERMELHO escuro = USADO (acabou) · VERMELHO = CANCELADO.
//   Amarelo + letra preta segue o padrão de destaque positivo da casa.
function SeloCredito({ status, saldo, utilizado }: { status: string; saldo: number; utilizado: number }) {
  let label: string;
  let bg: string;
  let fg: string;
  if (status === 'cancelado') {
    label = 'Cancelado'; bg = '#dc2626'; fg = '#fff';
  } else if (saldo <= 0.001 || status === 'consumido') {
    label = 'Usado'; bg = '#ef4444'; fg = '#000';
  } else if (utilizado > 0.001) {
    label = 'Parcial'; bg = '#facc15'; fg = '#000';
  } else {
    label = 'Disponível'; bg = '#facc15'; fg = '#000';
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 104, padding: '4px 12px', borderRadius: 999,
      background: bg, color: fg, fontSize: 11, fontWeight: 700,
      letterSpacing: 0.4, textTransform: 'uppercase', lineHeight: 1.4,
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

// Tabela de itens de um grupo (Devolvidos ou Levados) da troca.
// Extraído pra permitir intercalar o ExtratoCredito entre os dois grupos.
function GrupoItens({ itens, dir }: { itens: ItemDetalhe[]; dir: 'devolvido' | 'levado' }) {
  if (itens.length === 0) return null;
  const cor = dir === 'devolvido' ? '#16a34a' : '#2563eb';
  const titulo = dir === 'devolvido' ? 'Devolvidos' : 'Levados';
  const total = itens.reduce((s, i) => s + Number(i.subtotal), 0);
  return (
    <div>
      <h4 style={{ margin: '0 0 6px 0', fontSize: 13, color: cor }}>{titulo}</h4>
      <table className="tabela tabela-aninhada" style={{ width: '100%', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}></th>
            <th style={{ width: 70 }}>Cod</th>
            <th>Produto</th>
            <th style={{ textAlign: 'right', width: 70 }}>Qtd</th>
            <th style={{ textAlign: 'right', width: 90 }}>Unit.</th>
            <th style={{ textAlign: 'right', width: 100 }}>Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {itens.map((i) => (
            <tr key={i.id}>
              <td>{i.foto_url ? <img src={i.foto_url} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} /> : null}</td>
              <td className="text-gray-500 font-bold no-underline">{i.codigo_interno || '-'}</td>
              <td>{i.produto_descricao}</td>
              <td style={{ textAlign: 'right' }}>{Number(i.quantidade) % 1 === 0 ? Number(i.quantidade) : Number(i.quantidade).toFixed(3)}</td>
              <td style={{ textAlign: 'right' }}>{formatMoeda(i.preco_unitario)}</td>
              <td style={{ textAlign: 'right' }}>{formatMoeda(i.subtotal)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>Total {titulo.toLowerCase()}:</td>
            <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoeda(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

interface Props {
  onIrParaCreditos: () => void;
  onCorrigir: (id: number) => void;
}

export function HistoricoTrocas({ onIrParaCreditos, onCorrigir }: Props) {
  const { usuario } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  const [status, setStatus] = useState<'todas' | 'finalizada' | 'cancelada' | 'retornada' | 'disponivel'>('todas');
  const [validacao, setValidacao] = useState<'todas' | 'aguarda' | 'validada'>(
    (params.get('validacao') as 'todas' | 'aguarda' | 'validada') || 'todas',
  );
  const [tipo, setTipo] = useState<'todos' | 'troca' | 'devolucao' | 'compra_credito'>('todos');
  const [busca, setBusca] = useState('');
  const [lista, setLista] = useState<TrocaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [detalhe, setDetalhe] = useState<TrocaDetalhe | null>(null);
  const [cancelarTroca, setCancelarTrocaState] = useState<TrocaRow | null>(null);
  const [motivoCancel, setMotivoCancel] = useState('');
  const [retornarTroca, setRetornarTrocaState] = useState<TrocaRow | null>(null);
  const [zerarTroca, setZerarTrocaState] = useState<TrocaRow | null>(null);
  const [recibo, setRecibo] = useState<ReciboTrocaInfo | null>(null);
  const [acaoAndando, setAcaoAndando] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const [detalhesCache, setDetalhesCache] = useState<Record<number, TrocaDetalhe>>({});
  const [carregandoExpand, setCarregandoExpand] = useState<Set<number>>(new Set());

  async function toggleExpand(id: number) {
    setExpandidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
    if (!detalhesCache[id] && !carregandoExpand.has(id)) {
      setCarregandoExpand((prev) => new Set(prev).add(id));
      try {
        const r = await api.get<TrocaDetalhe>(`/api/trocas/${id}`);
        setDetalhesCache((prev) => ({ ...prev, [id]: r }));
      } catch (e: any) {
        toast.erro(e.message);
      } finally {
        setCarregandoExpand((prev) => { const n = new Set(prev); n.delete(id); return n; });
      }
    }
  }

  async function carregar() {
    setLoading(true);
    try {
      const r = await api.get<TrocaRow[]>('/api/trocas', { query: { status, validacao, tipo, limit: 300 } });
      setLista(r);
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void carregar(); }, [status, validacao, tipo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mantem URL em sincronia com filtro validacao (pra deep-link do banner)
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (validacao === 'todas') next.delete('validacao');
    else next.set('validacao', validacao);
    setParams(next, { replace: true });
  }, [validacao]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscaLower = busca.trim().toLowerCase();
  const filtrada = lista.filter((t) => {
    if (!buscaLower) return true;
    return t.cliente_nome.toLowerCase().includes(buscaLower) ||
           t.numero.toLowerCase().includes(buscaLower) ||
           (t.cliente_codigo ?? '').toLowerCase().includes(buscaLower);
  });

  async function abrirDetalhe(id: number) {
    try {
      const r = await api.get<TrocaDetalhe>(`/api/trocas/${id}`);
      setDetalhe(r);
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  function abrirRecibo(t: TrocaRow) {
    const ehCompra = t.tipo === 'compra_credito';
    setRecibo({
      id: t.id,
      numero: t.numero,
      clienteNome: t.cliente_nome,
      clienteCodigo: t.cliente_codigo,
      // Compra com credito nao tem saldo a favor: o destaque e' o que foi pago com credito.
      saldoCredito: ehCompra ? Number(t.valor_levado) : Number(t.credito_saldo ?? t.saldo_credito),
      rotuloSaldo: ehCompra ? 'Pago com credito:' : undefined,
      creditoNumero: null, // o numero do CRD vem no recibo gerado; header so destaca o saldo
      clienteWhatsapp: t.cliente_whatsapp || t.cliente_telefone || null,
    });
  }

  async function validarTroca(t: TrocaRow) {
    setAcaoAndando(true);
    try {
      await api.post(`/api/trocas/${t.id}/validar`, {});
      toast.sucesso(`Troca ${t.numero} validada`);
      void carregar();
      if (detalhe?.id === t.id) void abrirDetalhe(t.id);
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setAcaoAndando(false);
    }
  }

  async function confirmarCancelar() {
    if (!cancelarTroca) return;
    if (motivoCancel.trim().length < 3) { toast.erro('Informe o motivo (min 3 chars)'); return; }
    setAcaoAndando(true);
    try {
      await api.post(`/api/trocas/${cancelarTroca.id}/cancelar`, { motivo: motivoCancel.trim() });
      toast.sucesso(`Troca ${cancelarTroca.numero} cancelada`);
      setCancelarTrocaState(null);
      setMotivoCancel('');
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setAcaoAndando(false);
    }
  }

  async function confirmarZerarCredito() {
    if (!zerarTroca) return;
    setAcaoAndando(true);
    try {
      await api.post(`/api/trocas/${zerarTroca.id}/zerar-credito`, {});
      toast.sucesso(`Credito da troca ${zerarTroca.numero} zerado — cliente abriu mao do troco`);
      setZerarTrocaState(null);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setAcaoAndando(false);
    }
  }

  async function confirmarRetornar() {
    if (!retornarTroca) return;
    const trocaId = retornarTroca.id;
    const numero = retornarTroca.numero;
    setAcaoAndando(true);
    try {
      await api.post(`/api/trocas/${trocaId}/retornar`, {});
      toast.sucesso(`Troca ${numero} retornada para correcao`);
      setRetornarTrocaState(null);
      onCorrigir(trocaId); // ja leva pra aba Nova Troca em modo correcao
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setAcaoAndando(false);
    }
  }

  function statusBadge(t: TrocaRow) {
    if (t.status === 'cancelada') return <span className="badge-vermelho">Cancelada</span>;
    if (t.status === 'retornada') return <span style={{ fontSize: 11, padding: '2px 8px', background: '#fb923c', color: '#000', borderRadius: 4, fontWeight: 600 }}>Retornada</span>;
    if (!t.validada) return <span className="badge-amarelo">Aguarda validacao</span>;
    return <span className="badge-verde">Validada</span>;
  }

  // Selo do tipo. Pilula SOLIDA (o pastel antigo sumia no fundo azul do tema e obrigava
  // a ler linha a linha). Icone + palavra carregam o significado sozinhos — a cor so da
  // o peso pra bater o olho, entao nao depende de enxergar verde/vermelho.
  function tipoBadge(t: TrocaRow) {
    if (t.tipo === 'compra_credito') {
      return (
        <span title="Compra com credito" style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700,
          padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap', background: '#fde68a', color: '#000',
        }}>
          <ShoppingCart size={13} />
          COMPRA
        </span>
      );
    }
    const ehTroca = t.tipo === 'troca';
    const estilo = {
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700,
      padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap' as const,
      background: ehTroca ? '#dc2626' : '#a5f3fc',
      color: ehTroca ? '#fff' : '#000',
    };
    return (
      <span style={estilo}>
        {ehTroca ? <ArrowLeftRight size={13} /> : <Undo2 size={13} />}
        {ehTroca ? 'TROCA' : 'DEVOLUÇÃO'}
      </span>
    );
  }

  return (
    <div>
      {/* Filtros */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <label className="form-label" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as any)}>
            <option value="todas">Todas</option>
            <option value="disponivel">Disponível</option>
            <option value="finalizada">Finalizadas</option>
            <option value="retornada">Retornadas</option>
            <option value="cancelada">Canceladas</option>
          </select>
        </div>
        <div>
          <label className="form-label" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Validacao</label>
          <select value={validacao} onChange={(e) => setValidacao(e.target.value as any)}>
            <option value="todas">Todas</option>
            <option value="aguarda">Aguarda validacao</option>
            <option value="validada">Validadas</option>
          </select>
        </div>
        <div>
          <label className="form-label" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as any)}>
            <option value="todos">Todos</option>
            <option value="troca">Trocas</option>
            <option value="devolucao">Devolucoes</option>
            <option value="compra_credito">Compras com credito</option>
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="form-label" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Busca</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="search"
              placeholder="Cliente ou numero TRC-..."
              value={busca}
              onChange={(e) => setBusca(upperMantendoCursor(e))}
              style={{ width: '100%', padding: '6px 12px 6px 32px', border: '1px solid var(--input-border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--input-fg)' }}
            />
          </div>
        </div>
        <div style={{ alignSelf: 'flex-end', fontSize: 13, color: 'var(--text-muted)' }}>
          {filtrada.length} {filtrada.length === 1 ? 'troca' : 'trocas'}
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : filtrada.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhuma troca encontrada</div>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th style={{ width: 110 }}>Numero</th>
              <th style={{ width: 118 }}>Tipo</th>
              <th style={{ width: 60 }}>Codigo</th>
              <th>Cliente</th>
              <th style={{ width: 110 }}>Vendedor</th>
              <th style={{ width: 140 }}>Data</th>
              <th style={{ textAlign: 'right', width: 100 }}>Devolvido</th>
              <th style={{ textAlign: 'right', width: 100 }}>Levado</th>
              <th style={{ textAlign: 'right', width: 132 }}>Saldo</th>
              <th style={{ width: 150 }}>Status</th>
              <th style={{ width: 140 }}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {filtrada.map((t) => {
              const aberto = expandidos.has(t.id);
              const det = detalhesCache[t.id];
              const carregando = carregandoExpand.has(t.id);
              return (
                <Fragment key={t.id}>
                  <tr>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="btn-icon"
                        title={aberto ? 'Recolher itens' : 'Ver itens'}
                        onClick={() => void toggleExpand(t.id)}
                      >
                        {aberto ? <Minus size={14} /> : <Plus size={14} />}
                      </button>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{t.numero}</td>
                    <td>{tipoBadge(t)}</td>
                    <td><span className="codigo-curto">{t.cliente_codigo ?? '—'}</span></td>
                    <td>{t.cliente_nome}</td>
                    <td style={{ fontSize: 12 }}>{t.operador_nome ?? '—'}</td>
                    <td style={{ fontSize: 12 }}>{formatDataHoraSP(t.criado_em)}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoeda(t.valor_devolvido)}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoeda(t.valor_levado)}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                        <span>{formatMoeda(t.credito_saldo != null ? t.credito_saldo : t.saldo_credito)}</span>
                        {t.gerar_credito && Number(t.saldo_credito) > 0 && t.credito_status && (
                          <SeloCredito status={t.credito_status} saldo={Number(t.credito_saldo ?? 0)} utilizado={Number(t.credito_utilizado ?? 0)} />
                        )}
                      </div>
                    </td>
                    <td>{statusBadge(t)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {t.status !== 'cancelada' && (
                          <button type="button" className="btn-icon" onClick={() => abrirRecibo(t)} title="Imprimir comprovante" style={{ color: '#2563eb' }}>
                            <Printer size={14} />
                          </button>
                        )}
                        <button type="button" className="btn-icon" onClick={() => abrirDetalhe(t.id)} title="Ver detalhe">
                          <Eye size={14} />
                        </button>
                        {ehAdmin && t.status === 'finalizada' && !t.validada && (
                          <button
                            type="button"
                            className="btn-icon"
                            title="Validar troca"
                            onClick={() => validarTroca(t)}
                            disabled={acaoAndando}
                            style={{ color: '#16a34a' }}
                          >
                            <Check size={14} />
                          </button>
                        )}
                        {ehAdmin && t.status === 'finalizada' && !t.validada && t.tipo !== 'compra_credito' && (
                          <button
                            type="button"
                            className="btn-icon"
                            title="Retornar para correcao"
                            onClick={() => setRetornarTrocaState(t)}
                            disabled={acaoAndando}
                            style={{ color: '#d97706' }}
                          >
                            <Undo2 size={14} />
                          </button>
                        )}
                        {t.status === 'retornada' && (
                          <button
                            type="button"
                            className="btn-icon"
                            title="Corrigir troca"
                            onClick={() => onCorrigir(t.id)}
                            style={{ color: '#2563eb' }}
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        {t.status === 'finalizada' && t.gerar_credito && Number(t.saldo_credito) > 0
                          && t.credito_status === 'ativo' && Number(t.credito_utilizado ?? 0) < 0.001 && (
                          <button
                            type="button"
                            className="btn-icon"
                            title="Zerar credito (cliente abriu mao do troco)"
                            onClick={() => setZerarTrocaState(t)}
                            disabled={acaoAndando}
                            style={{ color: '#7c3aed' }}
                          >
                            <Eraser size={14} />
                          </button>
                        )}
                        {(t.status === 'finalizada' || t.status === 'retornada') && (
                          <button
                            type="button"
                            className="btn-icon"
                            title="Cancelar troca"
                            onClick={() => { setCancelarTrocaState(t); setMotivoCancel(''); }}
                            style={{ color: '#dc2626' }}
                          >
                            <Ban size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {aberto && (
                    <tr>
                      <td colSpan={11} style={{ background: 'var(--grid-row-alt-bg)', padding: 12 }}>
                        {carregando && !det ? (
                          <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando itens...</div>
                        ) : det ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 24, borderLeft: '3px solid var(--primary)', marginLeft: 8 }}>
                            {/* Trajetoria na ordem: Devolvidos -> Credito gerado/usado -> Levados */}
                            <GrupoItens itens={det.itens.filter((i) => i.direcao === 'devolvido')} dir="devolvido" />
                            {det.credito && (
                              <ExtratoCredito credito={det.credito} compacto />
                            )}
                            <CreditosUsados det={det} />
                            <GrupoItens itens={det.itens.filter((i) => i.direcao === 'levado')} dir="levado" />
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Modal detalhe */}
      <Modal aberto={!!detalhe} onFechar={() => setDetalhe(null)} titulo={detalhe ? `Troca ${detalhe.numero}` : ''} largura={900}>
        {detalhe && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 13 }}>
              <div><strong>Cliente:</strong> {detalhe.cliente_codigo ? <span className="codigo-curto" style={{ marginRight: 6 }}>{detalhe.cliente_codigo}</span> : null}{detalhe.cliente_nome}</div>
              <div><strong>Tipo:</strong> {detalhe.tipo === 'troca' ? 'Troca' : detalhe.tipo === 'compra_credito' ? 'Compra com credito' : 'Devolucao'}</div>
              <div><strong>Status:</strong> {statusBadge(detalhe)}</div>
              <div><strong>Data:</strong> {formatDataHoraSP(detalhe.criado_em)}</div>
              <div><strong>Operador:</strong> {detalhe.operador_nome || '-'}</div>
              {detalhe.validada && detalhe.validada_em && (
                <div><strong>Validada:</strong> {formatDataHoraSP(detalhe.validada_em)} por {detalhe.validada_por_nome || '-'}</div>
              )}
            </div>

            {detalhe.motivo && (
              <div style={{ fontSize: 13 }}><strong>Motivo:</strong> {detalhe.motivo}</div>
            )}
            {detalhe.observacoes && (
              <div style={{ fontSize: 13 }}><strong>Observacoes:</strong> {detalhe.observacoes}</div>
            )}
            {detalhe.cancelada_em && (
              <div style={{ padding: 8, background: 'rgba(220,38,38,0.08)', color: '#dc2626', borderRadius: 6, fontSize: 13 }}>
                <strong>Cancelada</strong> em {formatDataHoraSP(detalhe.cancelada_em)} por {detalhe.cancelada_por_nome || '-'}
                {detalhe.motivo_cancelamento && <div>Motivo: {detalhe.motivo_cancelamento}</div>}
              </div>
            )}

            {(['devolvido', 'levado'] as const).map((dir) => {
              const lista = detalhe.itens.filter((i) => i.direcao === dir);
              if (lista.length === 0) return null;
              const cor = dir === 'devolvido' ? '#16a34a' : '#2563eb';
              const titulo = dir === 'devolvido' ? 'Devolvidos' : 'Levados';
              const total = lista.reduce((s, i) => s + Number(i.subtotal), 0);
              return (
                <div key={dir}>
                  <h4 style={{ margin: '0 0 6px 0', fontSize: 14, color: cor }}>{titulo}</h4>
                  <table className="tabela" style={{ width: '100%', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th>
                        <th style={{ width: 70 }}>Cod</th>
                        <th>Produto</th>
                        <th style={{ textAlign: 'right', width: 70 }}>Qtd</th>
                        <th style={{ textAlign: 'right', width: 90 }}>Unit.</th>
                        <th style={{ textAlign: 'right', width: 100 }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lista.map((i) => (
                        <tr key={i.id}>
                          <td>{i.foto_url ? <img src={i.foto_url} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} /> : null}</td>
                          <td className="text-gray-500 font-bold no-underline">{i.codigo_interno || '-'}</td>
                          <td>{i.produto_descricao}</td>
                          <td style={{ textAlign: 'right' }}>{Number(i.quantidade) % 1 === 0 ? Number(i.quantidade) : Number(i.quantidade).toFixed(3)}</td>
                          <td style={{ textAlign: 'right' }}>{formatMoeda(i.preco_unitario)}</td>
                          <td style={{ textAlign: 'right' }}>{formatMoeda(i.subtotal)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: 'var(--grid-row-alt-bg)' }}>
                        <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>Total {titulo.toLowerCase()}:</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoeda(total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}

            {detalhe.tipo === 'compra_credito' ? (
              <CreditosUsados det={detalhe} />
            ) : (
            <div style={{ borderTop: '2px solid var(--border)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700 }}>
              <span>Saldo (devolvido - levado):</span>
              <span style={{ color: Number(detalhe.saldo_credito) > 0 ? '#16a34a' : Number(detalhe.saldo_credito) < 0 ? '#dc2626' : 'var(--text-muted)' }}>
                {formatMoeda(detalhe.saldo_credito)}
              </span>
            </div>
            )}

            {detalhe.credito && (
              <div>
                <ExtratoCredito credito={detalhe.credito} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                  <button type="button" className="btn-secundario" onClick={onIrParaCreditos}>
                    <ExternalLink size={14} style={{ display: 'inline', marginRight: 4 }} />
                    Ver Creditos
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-secundario" onClick={() => setDetalhe(null)}>Fechar</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal cancelar */}
      <Modal
        aberto={!!cancelarTroca}
        onFechar={() => { setCancelarTrocaState(null); setMotivoCancel(''); }}
        titulo={cancelarTroca ? `Cancelar troca ${cancelarTroca.numero}` : ''}
        largura={500}
      >
        {cancelarTroca && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13 }}>
              <div><strong>Cliente:</strong> {cancelarTroca.cliente_codigo ? <span className="codigo-curto" style={{ marginRight: 6 }}>{cancelarTroca.cliente_codigo}</span> : null}{cancelarTroca.cliente_nome}</div>
              <div><strong>Saldo:</strong> {formatMoeda(cancelarTroca.saldo_credito)}</div>
            </div>
            {cancelarTroca.tipo === 'compra_credito' ? (
            <div style={{ padding: 8, background: 'rgba(217,119,6,0.08)', color: '#92400e', borderRadius: 6, fontSize: 12 }}>
              <AlertTriangle size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Cancelar devolve os itens ao estoque, devolve {formatMoeda(cancelarTroca.valor_levado)} ao credito do cliente
              {cancelarTroca.orcamento_numero ? ` e reabre o orcamento ${cancelarTroca.orcamento_numero}` : ''}.
            </div>
            ) : (
            <div style={{ padding: 8, background: 'rgba(217,119,6,0.08)', color: '#92400e', borderRadius: 6, fontSize: 12 }}>
              <AlertTriangle size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Cancelar reverte estoque dos itens (devolvidos saem, levados voltam)
              {Number(cancelarTroca.saldo_credito) > 0 && cancelarTroca.gerar_credito && ' e cancela o credito gerado'}.
              {' '}Bloqueia se o credito ja foi consumido em alguma venda.
            </div>
            )}
            <div>
              <label className="form-label">Motivo do cancelamento</label>
              <textarea
                value={motivoCancel}
                onChange={(e) => setMotivoCancel(e.target.value)}
                rows={3}
                style={{ width: '100%' }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secundario" onClick={() => { setCancelarTrocaState(null); setMotivoCancel(''); }} disabled={acaoAndando}>
                Voltar
              </button>
              <button type="button" className="btn-primario" onClick={confirmarCancelar} disabled={acaoAndando}>
                {acaoAndando ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal retornar */}
      <Modal
        aberto={!!retornarTroca}
        onFechar={() => setRetornarTrocaState(null)}
        titulo={retornarTroca ? `Retornar troca ${retornarTroca.numero}` : ''}
        largura={500}
        compacto
      >
        {retornarTroca && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13 }}>
              <div><strong>Cliente:</strong> {retornarTroca.cliente_codigo ? <span className="codigo-curto" style={{ marginRight: 6 }}>{retornarTroca.cliente_codigo}</span> : null}{retornarTroca.cliente_nome}</div>
              <div><strong>Saldo:</strong> {formatMoeda(retornarTroca.saldo_credito)}</div>
            </div>
            <div style={{ padding: 8, background: 'rgba(217,119,6,0.08)', color: '#92400e', borderRadius: 6, fontSize: 12 }}>
              <AlertTriangle size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> A troca sai da fila de validacao e podera ser corrigida por voce ou pelo atendente. Estoque e credito nao mudam agora — so quando a correcao for salva.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secundario" onClick={() => setRetornarTrocaState(null)} disabled={acaoAndando}>
                Voltar
              </button>
              <button type="button" className="btn-primario" onClick={confirmarRetornar} disabled={acaoAndando}>
                {acaoAndando ? 'Retornando...' : 'Confirmar Retorno'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal zerar credito (cliente abriu mao do troco) */}
      <Modal
        aberto={!!zerarTroca}
        onFechar={() => setZerarTrocaState(null)}
        titulo={zerarTroca ? `Zerar credito da troca ${zerarTroca.numero}` : ''}
        largura={500}
        compacto
      >
        {zerarTroca && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13 }}>
              <div><strong>Cliente:</strong> {zerarTroca.cliente_codigo ? <span className="codigo-curto" style={{ marginRight: 6 }}>{zerarTroca.cliente_codigo}</span> : null}{zerarTroca.cliente_nome}</div>
              <div><strong>Troco a favor do cliente:</strong> {formatMoeda(zerarTroca.saldo_credito)}</div>
            </div>
            <div style={{ padding: 8, background: '#fde68a', color: '#000', borderRadius: 6, fontSize: 13, borderLeft: '4px solid #d97706' }}>
              <AlertTriangle size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
              O cliente abriu mao do troco. O credito de <strong>{formatMoeda(zerarTroca.saldo_credito)}</strong> sera cancelado e nao podera mais ser usado. A troca continua valida — estoque e itens nao mudam.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secundario" onClick={() => setZerarTrocaState(null)} disabled={acaoAndando}>
                Voltar
              </button>
              <button type="button" className="btn-primario" onClick={confirmarZerarCredito} disabled={acaoAndando}>
                {acaoAndando ? 'Zerando...' : 'Confirmar — cliente abriu mao'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal recibo (comprovante nao fiscal) */}
      {recibo && (
        <ModalReciboTroca recibo={recibo} onFechar={() => setRecibo(null)} />
      )}
    </div>
  );
}

